import React, {useMemo, useEffect, useRef} from 'react';
import Spinner from '@/components/ui/Spinner';
import {InlineAlert} from '@/components/alerts';
import Button from '@/components/ui/Button';
import {useChartExplainer} from '@/views/storyboard/hooks/useChartExplainer';
import {formatExplanation} from '@/views/storyboard/utils/formatExplanation';
import Icon from '@/components/icons/Icon';
import i18n from 'i18n';

interface ChartExplanationDisplayProps {
    chartId: number;
    isVisible: boolean;
    onToggleVisibility: () => void;
}

const ChartExplanationDisplay: React.FC<ChartExplanationDisplayProps> = ({
    chartId,
    isVisible,
    onToggleVisibility
}) => {
    const {isExplaining, explanation, error, streamingMessage, explainChart, clearExplanation, cancelExplanation} = useChartExplainer();
    const hasTriggeredRef = useRef(false);
    const hasAttemptedRef = useRef(false);

    useEffect(() => {
        if (isVisible && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            hasAttemptedRef.current = true;
            explainChart(chartId);
        }
    }, [isVisible, chartId, explainChart]);

    useEffect(() => {
        if (!isVisible) {
            hasTriggeredRef.current = false;
            hasAttemptedRef.current = false;
        }
    }, [isVisible]);

    const textAreaHeight = useMemo(() => {
        if (!explanation) {return '7.5em';}

        const formattedText = formatExplanation(explanation);
        const lineCount = formattedText.split('\n').filter(line => line.trim()).length;

        const batchedLines = Math.max(5, Math.ceil(lineCount / 5) * 5);

        return `${batchedLines * 1.5}em`;
    }, [explanation]);

    const handleExplainClick = async () => {
        if (!explanation && !error) {
            await explainChart(chartId);
        }
    };

    const handleRefreshClick = async () => {
        hasTriggeredRef.current = false;
        hasAttemptedRef.current = false;
        clearExplanation();
        await explainChart(chartId);
    };

    const handleCancelClick = () => {
        if (isExplaining) {
            cancelExplanation();
        }
        onToggleVisibility();
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className="chart-explanation-display">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                    <Icon name="mdi-brain" className="mr-1" size="12px" />
                    <span style={{fontSize: '11px', fontWeight: 'bold'}}>{i18n.t('ttl_chart_explanation.text')}</span>
                </div>
                <div className="chart-explanation-display__header-actions">
                    {explanation && !isExplaining && (
                        <Button
                            variant="outline-info"
                            size="xs"
                            className="chart-explanation-display__header-btn"
                            onClick={handleRefreshClick}
                            disabled={isExplaining}
                            style={{height: '22px', minHeight: '22px', padding: '0 6px'}}
                        >
                            <Icon name="mdi-refresh" size="10px" className="mr-1" />
                            Refresh
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="xs"
                        className="chart-explanation-display__header-btn chart-explanation-display__header-btn--icon-only"
                        onClick={handleCancelClick}
                        style={{height: '22px', minHeight: '22px', width: '22px', minWidth: '22px', padding: 0}}
                    >
                        <Icon name="mdi-close" size="10px" />
                    </Button>
                </div>
            </div>

            {isExplaining && !explanation && (
                <div className="text-center py-2">
                    <Spinner
                        variant="border"
                        size="sm"
                        text={streamingMessage || 'Thinking...'}
                        textPosition="below"
                    />
                </div>
            )}

            {error && (
                <InlineAlert
                    variant="error"
                    message={error}
                    className="mb-2"
                />
            )}

            {explanation && (
                <div
                    style={{
                        whiteSpace: 'pre-line',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        maxHeight: '120px',
                        overflowY: 'auto'
                    }}
                >
                    {formatExplanation(explanation)}
                </div>
            )}


            {(error || (hasAttemptedRef.current && !explanation && !isExplaining)) && (
                <div className="text-center py-2">
                    <Button
                        variant="primary"
                        size="xs"
                        onClick={handleExplainClick}
                        disabled={isExplaining}
                    >
                        <Icon name="mdi-brain" className="mr-1" />
                        {i18n.t('btn_start_analysis.text')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ChartExplanationDisplay;
